import { useCallback, useState } from "react";
import OverlayTable from "./OverlayTable";
import ProductRing from "./ProductRing";
import NodeColorLegend from "./NodeColorLegend";
import { OpportunitySizeOption } from "../growth/utils";
import { colorByToLegendLabel } from "@atlas-int/viz";
import ComplexityColorScaleLegend from "../treemap/components/ComplexityColorScaleLegend";
import { CategoryLabels } from "@atlas-int/viz/shared";
import { text, surface, fontWeight, fontSizeValues, spacing } from "@atlas-int/tokens";
import { TooltipStateBridge } from "../../components/TooltipStateBridge";
import { color } from "d3-color";
import { moneyFormatter } from "@atlas-int/data";
import {
  TooltipContentsContainer,
  TooltipHeader,
  TooltipBody as SharedTooltipBody,
  TooltipBodyRow,
  TooltipBodyRowLabel,
  TooltipBodyRowValue,
  ProductCodeLabel,
  SectorLabelWithColorIcon,
} from "../../components/tooltip/SharedTooltipComponents";
import type { TooltipState, TooltipDatum, TooltipProductDatum } from "../../types/atlasTooltip";
import type { ProcessedNode } from "./types";
import type { CategoryDatum } from "@atlas-int/viz/shared";
import type { HierarchyNode } from "d3-hierarchy";
import type { ProductMetadatum } from "@atlas-int/data/types";
import { ColorBy } from "@atlas-int/data";

type MetaDataNode = HierarchyNode<ProductMetadatum>;

interface ProductOverlayProps {
  linkLookup: Record<string, string[]>;
  selection: string;
  nodeLookup: Map<string, ProcessedNode>;
  metaDataLookup: Map<string, MetaDataNode>;
  colorMap: Map<string, string>;
  setRingItem: (value: string | null) => void;
  displayData: unknown;
  totalExportLookup: Map<string, { exportValue?: number | null; pci?: number | null }>;
  countryExportLookup: Map<string, { exportRca?: number | null; exportValue?: number | null }>;
  colorBySelection: ColorBy;
  clusterColorLookup: Map<string, { cluster_col?: string }>;
  complexityColorScale: (value: number | null | undefined) => string;
  rcaThreshold: number;
  categories: CategoryDatum[];
  sizing: string | undefined;
  productClass: string;
  year: number | undefined;
  renderModal: (props: { onClose: () => void; children: React.ReactNode }) => React.ReactNode;
}

const ProductOverlay = ({
  linkLookup,
  selection,
  nodeLookup,
  metaDataLookup,
  colorMap,
  setRingItem,
  displayData,
  totalExportLookup,
  countryExportLookup,
  colorBySelection,
  clusterColorLookup,
  complexityColorScale,
  rcaThreshold,
  categories,
  sizing,
  productClass,
  year,
  renderModal,
}: ProductOverlayProps) => {
  const [highlighted, highlight] = useState(null);

  const getSizingLegendText = () => {
    switch (sizing) {
      case OpportunitySizeOption.CountryTrade:
        return " based on Country Exports";
      case OpportunitySizeOption.WorldTrade:
        return " based on Global Exports";
      default:
        return "";
    }
  };

  const renderExplanation = useCallback(
    (tooltipState: TooltipState<TooltipDatum> | undefined) => {
      if (!tooltipState?.contentData) return null;
      const datum = tooltipState.contentData;
      if (datum.kind !== "product") return null;
      const d = datum as TooltipProductDatum;
      const sectorName = metaDataLookup.get(
        d.topLevelParent as string,
      )?.data?.nameShortEn;
      const fillColor = colorMap.get(d.topLevelParent as string);
      const hexColor = color(fillColor)?.formatHex() ?? "#000000";
      return (
        <TooltipContentsContainer>
          <TooltipHeader $backgroundColor={hexColor}>
            <div>
              {d.nameEn} |{" "}
              <ProductCodeLabel>
                {d.productCode} {productClass}
              </ProductCodeLabel>
            </div>
          </TooltipHeader>
          <SharedTooltipBody>
            <TooltipBodyRow>
              <TooltipBodyRowLabel>Sector:</TooltipBodyRowLabel>
              <TooltipBodyRowValue>
                <SectorLabelWithColorIcon $fillColor={hexColor}>
                  {sectorName}
                </SectorLabelWithColorIcon>
              </TooltipBodyRowValue>
            </TooltipBodyRow>
            <TooltipBodyRow>
              <TooltipBodyRowLabel>Distance:</TooltipBodyRowLabel>
              <TooltipBodyRowValue>
                {d.distance ? d.distance.toFixed(3) : "N/A"}
              </TooltipBodyRowValue>
            </TooltipBodyRow>
            <TooltipBodyRow>
              <TooltipBodyRowLabel>Product Complexity Index (PCI):</TooltipBodyRowLabel>
              <TooltipBodyRowValue>{d.pci?.toFixed(3)}</TooltipBodyRowValue>
            </TooltipBodyRow>
            <TooltipBodyRow>
              <TooltipBodyRowLabel>Revealed Comparative Advantage (RCA):</TooltipBodyRowLabel>
              <TooltipBodyRowValue>
                {d.exportRca ? d.exportRca.toFixed(3) : "N/A"}
              </TooltipBodyRowValue>
            </TooltipBodyRow>
            <TooltipBodyRow>
              <TooltipBodyRowLabel>World Trade:</TooltipBodyRowLabel>
              <TooltipBodyRowValue>
                {d.exportValue ? moneyFormatter.format(d.exportValue) : "N/A"}
              </TooltipBodyRowValue>
            </TooltipBodyRow>
          </SharedTooltipBody>
        </TooltipContentsContainer>
      );
    },
    [metaDataLookup, productClass, colorMap],
  );

  return (
    <>{renderModal({ onClose: () => setRingItem(null), children: (
      <TooltipStateBridge renderExplanation={renderExplanation} overrideStyles={true} flexWidth>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: spacing[6],
          height: "min(70vh, 700px)",
          minHeight: 360,
          width: "100%",
        }}
      >
        <div
          style={{
            flex: "1 1 55%",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <div
            style={{
              flex: "1 1 auto",
              minHeight: 0,
              minWidth: 0,
              position: "relative",
            }}
          >
            <ProductRing
              linkLookup={linkLookup}
              selection={selection}
              nodeLookup={nodeLookup}
              metaDataLookup={metaDataLookup}
              setRingItem={setRingItem}
              colorMap={colorMap}
              highlighted={highlighted}
              highlight={highlight}
              colorBySelection={colorBySelection}
              clusterColorLookup={clusterColorLookup}
              complexityColorScale={complexityColorScale}
              totalExportLookup={totalExportLookup}
              countryExportLookup={countryExportLookup}
              rcaThreshold={rcaThreshold}
              productClass={productClass}
            />
          </div>
          <div
            style={{
              flexShrink: 0,
              display: "flex",
              justifyContent: "center",
              padding: `${spacing[2]} ${spacing[4]}`,
            }}
          >
            {colorBySelection === ColorBy.Complexity ? (
              <ComplexityColorScaleLegend smallFont={true} />
            ) : (
              <CategoryLabels
                categories={categories}
                allowToggle={false}
                legendLabel={colorByToLegendLabel[colorBySelection]}
                smallFont={true}
              />
            )}
          </div>
        </div>
        <div
          style={{
            flex: "1 1 45%",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <div style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
            <OverlayTable
              colorMap={colorMap}
              metaDataLookup={metaDataLookup}
              totalExportLookup={totalExportLookup}
              displayData={displayData as Record<string, unknown>}
              selection={selection}
              linkLookup={linkLookup}
              nodeLookup={nodeLookup}
              countryExportLookup={countryExportLookup}
              highlight={highlight}
              setRingItem={setRingItem}
              colorBySelection={colorBySelection}
              clusterColorLookup={clusterColorLookup}
              complexityColorScale={complexityColorScale}
              rcaThreshold={rcaThreshold}
              year={year}
              productClass={productClass}
            />
          </div>
          {colorBySelection !== ColorBy.Complexity && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-around",
                background: surface.white,
              }}
            >
              <NodeColorLegend />
              {sizing !== OpportunitySizeOption.None && (
                <div style={{ marginLeft: spacing[10] }}>
                  <span
                    style={{
                      fontSize: fontSizeValues.xs,
                      marginBottom: spacing[4],
                      fontWeight: fontWeight.bold,
                    }}
                  >
                    Node Sizing
                  </span>
                  <span style={{ fontSize: fontSizeValues.xs, color: text.primary }}>
                    {getSizingLegendText()}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </TooltipStateBridge>
    ) })}</>
  );
};

export default ProductOverlay;
